import { useEffect, useRef, useState } from 'react';

/**
 * The direction of the last vertical scroll.
 *
 * Subscribes once, for the life of the component. Both the last position and the
 * pending-frame flag live in refs rather than state or closure, so the effect has
 * no dependencies: an earlier version depended on the direction it produced, which
 * meant every change tore the listener down, re-attached it, and reset the
 * reference position mid-scroll.
 *
 * One rAF in flight at a time. Scroll fires far faster than the browser paints, so
 * queuing a frame per event schedules dozens of redundant callbacks during a fast
 * scroll.
 */
const useScrollDirection = () => {
  const [scrollDir, setScrollDir] = useState<'up' | 'down'>('up');
  const lastY = useRef(0);
  const frameRequested = useRef(false);

  useEffect(() => {
    lastY.current = window.scrollY;

    const update = () => {
      frameRequested.current = false;

      const currentY = window.scrollY;

      // Ignore rubber-band overscroll past the top, which otherwise reads as a
      // direction change at the very moment the header should stay put.
      if (currentY < 0) return;

      setScrollDir(currentY > lastY.current ? 'down' : 'up');
      lastY.current = currentY;
    };

    const onScroll = () => {
      if (frameRequested.current) return;

      frameRequested.current = true;
      window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return scrollDir;
};

export default useScrollDirection;
